import { NgButton } from "@/atoms";
import { FlagIcon } from "@/atoms/icons/FlagIcon";
import { IconChevronDown } from "@tabler/icons-react";
import { keyBy } from "lodash-es";
import { useState } from "react";

import {
  ISO_3166_ALPHA_2_MAPPINGS,
  Iso3166Alpha2Code,
  RiskOption,
} from "@oneleet/utils";

import { NgDropdownMenu } from "../DropdownMenu";

const riskOptionForCountryCode = (code: Iso3166Alpha2Code): RiskOption => ({
  id: code,
  label: ISO_3166_ALPHA_2_MAPPINGS[code],
  risk: "LOW",
});

export function CountriesDropdown({
  disabled,

  initialCountries = [],
  onCountriesChanged,
}: {
  disabled?: boolean;

  initialCountries?: Iso3166Alpha2Code[];
  onCountriesChanged: (countries: Iso3166Alpha2Code[]) => void;
}) {
  // 2025-04-28
  // Country codes from: https://www.iban.com/country-codes
  // Sorted by: https://www.visualcapitalist.com/ranked-the-top-25-countries-with-the-most-data-centers/
  const suggestedCountryCodes: Iso3166Alpha2Code[] = [
    "US",
    "DE",
    "GB",
    "CN",
    "CA",
    "FR",
    "AU",
    "NL",
    "RU",
    "JP",
    "MX",
    "IT",
    "BR",
    "IN",
    "PL",
    "ES",
    "HK",
    "CH",
    "SG",
    "SE",
  ];
  const suggestedCountryOptions: RiskOption[] = suggestedCountryCodes.map(
    riskOptionForCountryCode,
  );
  const otherCountryOptions: RiskOption[] = (
    Object.keys(ISO_3166_ALPHA_2_MAPPINGS) as Iso3166Alpha2Code[]
  )
    .filter((code) => !suggestedCountryCodes.includes(code))
    .map(riskOptionForCountryCode);
  const countryOptions = [...suggestedCountryOptions, ...otherCountryOptions];
  const countryOptionsById = keyBy(countryOptions, "id");

  const [selectedIds, setSelectedIds] =
    useState<Iso3166Alpha2Code[]>(initialCountries);

  return (
    <div className="flex flex-col gap-2">
      <NgDropdownMenu>
        <NgDropdownMenu.Trigger asChild disabled={disabled}>
          <div className="flex items-center gap-2">
            <NgButton
              variant="outlined"
              endIcon={<IconChevronDown />}
              disabled={disabled}
              className="me-2"
            >
              {(() => {
                if (selectedIds.length === 0) return "Select countries";
                return (
                  selectedIds
                    ?.slice(0, 2)
                    .map((id) => countryOptionsById[id]?.label)
                    .join(", ") +
                  (selectedIds.length > 2
                    ? `, + ${selectedIds.length - 2} more`
                    : "")
                );
              })()}
            </NgButton>

            {selectedIds &&
              selectedIds.map((id) => (
                <FlagIcon
                  key={id}
                  countryCode={id}
                  countryName={countryOptionsById[id]?.label}
                  size="sm"
                />
              ))}
          </div>
        </NgDropdownMenu.Trigger>
        <NgDropdownMenu.Content align="start" style={{ maxHeight: "30em" }}>
          {[
            { sectionLabel: "Suggested Countries" },
            ...suggestedCountryOptions,
            { sectionLabel: "Other Countries" },
            ...otherCountryOptions,
          ].map((option, index) =>
            "sectionLabel" in option ? (
              <>
                {index > 0 && (
                  <NgDropdownMenu.Separator key={`${index}-separator`} />
                )}
                <NgDropdownMenu.Label key={`${index}-label`}>
                  {option.sectionLabel}
                </NgDropdownMenu.Label>
              </>
            ) : (
              <NgDropdownMenu.CheckboxItem
                key={option.id}
                checked={selectedIds?.includes(option.id as Iso3166Alpha2Code)}
                onCheckedChange={(checked) => {
                  if (disabled) return;

                  let newSelectedIds: Iso3166Alpha2Code[];
                  if (checked) {
                    // Add the new country, preserving the dropdown order
                    newSelectedIds = countryOptions
                      .filter((country) => {
                        const countryId = country.id as Iso3166Alpha2Code;
                        return (
                          countryId === option.id ||
                          selectedIds.includes(countryId)
                        );
                      })
                      .map((country) => country.id) as Iso3166Alpha2Code[];
                  } else {
                    newSelectedIds = selectedIds.filter(
                      (id) => id !== option.id,
                    );
                  }

                  setSelectedIds(newSelectedIds);
                  onCountriesChanged(newSelectedIds);
                }}
                keepOpenOnSelect
              >
                <div className="flex items-center gap-2">
                  <FlagIcon
                    key={option.id}
                    countryCode={option.id}
                    countryName={countryOptionsById[option.id]?.label}
                    size="sm"
                  />
                  {option.label}
                </div>
              </NgDropdownMenu.CheckboxItem>
            ),
          )}
        </NgDropdownMenu.Content>
      </NgDropdownMenu>
    </div>
  );
}
